<template>
  <component :is="shell">
    <h1 v-if="!embedded">选型指导</h1>
    <p v-if="!embedded" class="sub">描述训练、推理、国产或预算。推荐只用卡库里的参数和价格，有推荐扣 1 积分，同一句话不重复扣。</p>

    <form v-if="!embedded" class="composer" @submit.prevent="submit">
      <textarea v-model="text" rows="4" placeholder="例如：国产，训练 70B，预算 80 万" />
      <button type="submit" :disabled="loading || processActive">{{ loading || processActive ? '选型中…' : '开始选型' }}</button>
    </form>
    <div v-if="!embedded" class="samples">
      <button v-for="item in samples" :key="item" type="button" @click="useSample(item)">{{ item }}</button>
    </div>

    <CreditNext v-if="gateStatus" :status="gateStatus" :message="error" />
    <p v-else-if="error" class="error">{{ error }}</p>
    <QueryProcessRail
      v-if="processSteps.length"
      :steps="processSteps"
      :active="processActive"
      :title="processActive ? '正在规划选型步骤…' : '本次选型过程'"
    />

    <section v-if="result" class="result">
      <p v-if="result.understand">{{ result.understand }}</p>
      <p v-for="(note, index) in result.assumptions || []" :key="index" class="muted">{{ note }}</p>
      <p v-if="result.reason" class="muted">{{ result.reason }}</p>
      <p v-if="result.gap" class="muted">{{ result.gap }}</p>
      <p v-if="charged" class="charge">本次已扣 1 积分</p>
      <QuoteAsk kind="advise" :summary="inquirySummary" :detail="inquiryDetail" />

      <article v-if="result.primary" class="pick">
        <p class="role">首选</p>
        <CardPick :card="result.primary" />
      </article>
      <article v-for="card in result.alternatives || []" :key="card.id" class="pick alt">
        <p class="role">备选</p>
        <CardPick :card="card" />
      </article>
    </section>
  </component>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import CreditNext from '../components/CreditNext.vue'
import CardPick from '../components/CardPick.vue'
import QuoteAsk from '../components/QuoteAsk.vue'
import QueryProcessRail from '../components/QueryProcessRail.vue'
import { createAdviseJob, getAdviseJob } from '../api/platformService'
import { readEvents } from '../api/sse'
import { notifyAuthChanged } from '../utils/authBus'
import {
  buildAdviseProcessSteps,
  finishAdviseProcessSteps
} from '../utils/queryProcessScript'

export default {
  name: 'AdviseWorkspace',
  components: { AppShell, CreditNext, CardPick, QuoteAsk, QueryProcessRail },
  props: {
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      text: '',
      loading: false,
      error: '',
      gateStatus: 0,
      step: '',
      result: null,
      charged: false,
      stream: null,
      jobId: null,
      processSteps: [],
      processActive: false,
      samples: ['国产，训练 70B，预算 80 万', '显存至少 80GB，FP16 300T 以上', 'H100 的价格和参数']
    }
  },
  computed: {
    shell() {
      return this.embedded ? 'div' : AppShell
    },
    inquirySummary() {
      const name = this.result?.primary?.name
      return name ? `选型首选 ${name}` : '选型报价'
    },
    inquiryDetail() {
      const result = this.result || {}
      return {
        text: this.text,
        understand: result.understand || '',
        reason: result.reason || '',
        primary: result.primary?.name || '',
        alternatives: (result.alternatives || []).map((card) => card.name).filter(Boolean)
      }
    }
  },
  mounted() {
    const id = this.$route.query?.job
    if (id) this.openJob(id)
  },
  beforeUnmount() {
    if (this.stream) this.stream.abort()
  },
  methods: {
    useSample(item) {
      this.text = item
      this.submit()
    },
    syncProcess(serverStep = '', { done = false, clear = false } = {}) {
      if (clear) {
        this.processSteps = []
        this.processActive = false
        return
      }
      const steps = buildAdviseProcessSteps(serverStep)
      this.processSteps = done ? finishAdviseProcessSteps(steps) : steps
      this.processActive = !done
    },
    async openJob(id) {
      this.error = ''
      this.gateStatus = 0
      this.jobId = Number(id) || id
      try {
        const job = await getAdviseJob(id)
        this.text = job.text || this.text
        this.step = job.step || ''
        this.jobId = job.id || this.jobId
        if (job.status === 'done') {
          this.result = job.result || null
          this.charged = Boolean(job.result && job.result.charged)
          this.step = ''
          this.syncProcess('结论', { done: true })
          return
        }
        if (job.status === 'failed') {
          this.error = job.error || '选型没有完成'
          this.syncProcess(job.step || '', { done: true })
          return
        }
        this.syncProcess(job.step || '已提交')
        this.watchJob(id)
      } catch (err) {
        const status = err.response?.status
        this.gateStatus = status === 401 || status === 402 ? status : 0
        this.error = err.response?.data?.error || '任务打不开'
        this.syncProcess('', { clear: true })
      }
    },
    watchJob(id) {
      if (this.stream) this.stream.abort()
      const controller = new AbortController()
      this.stream = controller
      this.jobId = Number(id) || id
      readEvents(`/api/agent/jobs/${id}/events`, async (event) => {
        this.step = event.step || ''
        this.syncProcess(event.step || this.step)
        if (event.status === 'failed') {
          this.error = event.error || '选型没有完成'
          this.step = ''
          this.syncProcess(event.step || '', { done: true })
          return
        }
        if (event.status !== 'done') return
        const job = await getAdviseJob(id)
        this.jobId = job.id || this.jobId
        this.result = job.result || null
        this.charged = Boolean(job.result && job.result.charged)
        this.step = ''
        this.syncProcess('结论', { done: true })
        if (this.charged) notifyAuthChanged()
      }, controller.signal).then(async () => {
        if (this.result || this.error) return
        const job = await getAdviseJob(id)
        if (job.status === 'done') {
          this.jobId = job.id || this.jobId
          this.result = job.result || null
          this.charged = Boolean(job.result && job.result.charged)
          this.step = ''
          this.syncProcess('结论', { done: true })
          if (this.charged) notifyAuthChanged()
        } else if (job.status === 'failed') {
          this.error = job.error || '选型没有完成'
          this.step = ''
          this.syncProcess(job.step || '', { done: true })
        }
      }).catch((err) => {
        if (err.name === 'AbortError') return
        this.gateStatus = err.status === 401 || err.status === 402 ? err.status : 0
        this.error = err.message || '进度连接失败'
        this.processActive = false
      })
    },
    async submit() {
      const text = this.text.trim()
      if (!text) {
        this.error = '请写下选型需求'
        return
      }
      this.loading = true
      this.error = ''
      this.gateStatus = 0
      this.result = null
      this.charged = false
      this.jobId = null
      this.step = '已提交'
      this.syncProcess('已提交')
      try {
        const job = await createAdviseJob(text)
        this.jobId = job.id
        this.$router.replace({ path: '/advise', query: { job: String(job.id) } }).catch(() => {})
        this.watchJob(job.id)
      } catch (err) {
        const status = err.response?.status
        this.gateStatus = status === 401 || status === 402 ? status : 0
        this.error = err.response?.data?.error || '选型没有提交'
        this.step = ''
        this.syncProcess('', { clear: true })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
h1 { margin: 0 0 8px; font-size: 34px; font-weight: 600; letter-spacing: -0.03em; }
.sub { margin: 0 0 18px; color: #86909c; font-size: 15px; max-width: 640px; }
.composer { display: flex; gap: 10px; align-items: stretch; max-width: 720px; }
textarea {
  flex: 1;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  padding: 14px 16px;
  font: inherit;
  font-size: 15px;
  resize: none;
  min-height: 96px;
}
textarea:focus { outline: none; border-color: #165dff; }
.composer button, .samples button {
  border: 0;
  background: #165dff;
  color: #fff;
  border-radius: 16px;
  padding: 0 22px;
  font-weight: 600;
  cursor: pointer;
}
.composer button:disabled { opacity: 0.6; }
.samples { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.samples button {
  background: #fff;
  color: #4e5969;
  border: 1px solid #e5e6eb;
  border-radius: 999px;
  padding: 6px 12px;
  font-weight: 400;
}
.step, .error, .charge, .muted { font-size: 13px; margin: 12px 0; }
.step { color: #4e5969; }
.error { color: #f53f3f; }
.charge { color: #188038; }
.muted { color: #86909c; }
.pick {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  padding: 16px 18px;
  margin-top: 12px;
}
.role { margin: 0 0 6px; color: #165dff; font-size: 12px; font-weight: 600; }
</style>
